import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '@/components/base/Button';
import Badge, { type BadgeTone } from '@/components/base/Badge';
import { api, ApiError } from '@/utils/api';
import { formatDateTime } from '@/utils/format';

/**
 * Developer and trainer tool, not a product screen: the creator notifications the
 * API saved instead of sending (NOTIFY_MODE=log). Reached at /dev/outbox only; the
 * API answers 404 when notifications are sent for real.
 */

type Channel = 'email' | 'sms' | 'push';

interface OutboxEntry {
  id: string;
  at: string;
  channel: Channel;
  to: string;
  subject: string;
  text: string;
  note: string;
}

const channelMeta: Record<Channel, { label: string; icon: string; tone: BadgeTone }> = {
  email: { label: 'Email', icon: 'ri-mail-line', tone: 'primary' },
  sms: { label: 'SMS', icon: 'ri-message-2-line', tone: 'accent' },
  push: { label: 'Push', icon: 'ri-notification-3-line', tone: 'secondary' },
};

const filters: { id: 'all' | Channel; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'email', label: 'Email' },
  { id: 'sms', label: 'SMS' },
  { id: 'push', label: 'Push' },
];

export default function Outbox() {
  const [entries, setEntries] = useState<OutboxEntry[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'off' | 'error'>('loading');
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | Channel>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [html, setHtml] = useState('');
  const [clearing, setClearing] = useState(false);

  const load = useCallback(() => {
    setState((current) => (current === 'ready' ? current : 'loading'));
    api<OutboxEntry[]>('/dev/outbox')
      .then((data) => {
        setEntries(data);
        setState('ready');
      })
      .catch((err: ApiError) => {
        setError(err.message);
        setState(err.status === 404 ? 'off' : 'error');
      });
  }, []);

  useEffect(load, [load]);

  const visible = useMemo(
    () => (filter === 'all' ? entries : entries.filter((entry) => entry.channel === filter)),
    [entries, filter],
  );
  const selected = entries.find((entry) => entry.id === selectedId) ?? null;

  // Emails keep their HTML on the server; fetch it when one is opened.
  useEffect(() => {
    setHtml('');
    if (!selected || selected.channel !== 'email') return;
    let active = true;
    api<OutboxEntry & { html: string }>(`/dev/outbox/${encodeURIComponent(selected.id)}`)
      .then((data) => {
        if (active) setHtml(data.html);
      })
      .catch(() => {
        if (active) setHtml('');
      });
    return () => {
      active = false;
    };
  }, [selected]);

  const clear = async () => {
    setClearing(true);
    try {
      await api('/dev/outbox', { method: 'DELETE' });
      setEntries([]);
      setSelectedId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not clear the outbox.');
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <span className="page-rule" aria-hidden="true" />
          <p className="text-xs font-label font-medium uppercase tracking-wider text-foreground-400">
            Developer · Notifications
          </p>
          <h1 className="mt-1 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">Outbox</h1>
          <p className="mt-1 max-w-2xl text-sm text-foreground-600">
            Emails, SMS and push notifications creators would have received. In this environment
            nothing is sent; messages are kept here instead.
          </p>
        </div>
        {state === 'ready' ? (
          <div className="flex gap-2">
            <Button variant="outline" icon="ri-refresh-line" onClick={load}>
              Refresh
            </Button>
            <Button
              variant="ghost"
              icon="ri-delete-bin-line"
              onClick={() => void clear()}
              disabled={clearing || entries.length === 0}
            >
              {clearing ? 'Clearing…' : 'Clear'}
            </Button>
          </div>
        ) : null}
      </div>

      {state === 'loading' ? (
        <p className="mt-8 text-sm text-foreground-500">Loading…</p>
      ) : state === 'off' ? (
        <div className="mt-8 rounded-lg border border-background-200 bg-background-50 p-6 text-sm text-foreground-600">
          <i className="ri-send-plane-line mr-1" />
          {error}
        </div>
      ) : state === 'error' ? (
        <div role="alert" className="mt-8 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
          <i className="ri-error-warning-line mr-1" />
          Couldn&apos;t load the outbox: {error}
        </div>
      ) : (
        <>
          <div className="mt-6 flex flex-wrap gap-1.5" role="tablist" aria-label="Channel">
            {filters.map((item) => {
              const count = item.id === 'all' ? entries.length : entries.filter((entry) => entry.channel === item.id).length;
              const active = filter === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setFilter(item.id)}
                  className={`rounded-full border px-3 py-1 text-xs font-label cursor-pointer ${
                    active
                      ? 'border-foreground-900 bg-foreground-900 text-background-50'
                      : 'border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  {item.label} · {count}
                </button>
              );
            })}
          </div>

          {visible.length === 0 ? (
            <div className="mt-6 rounded-lg border border-dashed border-background-300 px-6 py-12 text-center">
              <i className="ri-inbox-line text-2xl text-foreground-400" />
              <p className="mt-2 text-sm text-foreground-600">No messages yet.</p>
              <p className="mt-1 text-xs text-foreground-500">
                Invite creators to a new campaign, send a brief, decline an applicant, or approve or
                request changes on a deliverable.
              </p>
            </div>
          ) : (
            <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
              <ul className="divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
                {visible.map((entry) => {
                  const meta = channelMeta[entry.channel];
                  const active = entry.id === selectedId;
                  return (
                    <li key={entry.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(entry.id)}
                        className={`flex w-full flex-col gap-1 px-4 py-3 text-left cursor-pointer ${
                          active ? 'bg-background-100' : 'hover:bg-background-100'
                        }`}
                      >
                        <span className="flex items-center justify-between gap-2">
                          <Badge tone={meta.tone} icon={meta.icon}>
                            {meta.label}
                          </Badge>
                          <span className="text-[11px] text-foreground-500">{formatDateTime(entry.at)}</span>
                        </span>
                        <span className="truncate text-sm font-medium text-foreground-900">{entry.subject}</span>
                        <span className="truncate text-xs text-foreground-500">
                          To {entry.to}
                          {entry.note ? ` · ${entry.note}` : ''}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>

              <div className="min-h-[320px] rounded-lg border border-background-200 bg-background-50">
                {!selected ? (
                  <p className="px-6 py-12 text-center text-sm text-foreground-500">
                    Select a message to see it as the creator would.
                  </p>
                ) : (
                  <div className="flex h-full flex-col">
                    <div className="border-b border-background-200 px-4 py-3">
                      <p className="text-sm font-medium text-foreground-900">{selected.subject}</p>
                      <p className="mt-0.5 text-xs text-foreground-500">
                        {channelMeta[selected.channel].label} to {selected.to} · {formatDateTime(selected.at)}
                        {selected.note ? ` · ${selected.note}` : ''}
                      </p>
                    </div>
                    {selected.channel === 'email' ? (
                      html ? (
                        <iframe
                          title={`Email: ${selected.subject}`}
                          srcDoc={html}
                          sandbox=""
                          className="h-[640px] w-full flex-1 rounded-b-lg bg-background-50"
                        />
                      ) : (
                        <p className="px-4 py-6 text-sm text-foreground-500">Loading the email…</p>
                      )
                    ) : (
                      <p className="whitespace-pre-wrap px-4 py-6 text-sm leading-relaxed text-foreground-800">
                        {selected.text}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
